import React, { useState, useEffect } from "react";
import { toast } from "../modern-ui/sonner";
import { useScrollLock } from "../../hooks/useScrollLock";
import {
  ModalOverlay,
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  ModalSubtitle,
  Section,
  SectionContent,
  SectionTitle,
  SectionDescription,
  TimerContainer,
  TimerLabel,
  ReferralLinkContainer,
  ReferralLink,
  CopyButton,
  ProgressContainer,
  ProgressText,
  CollectButton,
  Row,
  StyledRow,
  Divider,
  ProgressBar,
  ProgressFill,
  SectionDescriptionFirst,
  SectionDescriptionSecond,
} from "./FreeCreditsModal.styles";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAddDailyCreditsLoading,
  selectUser,
} from "../../store/slices/user/slice";
import { addDailyCredits } from "../../store/slices/user/asyncThunk";
import { MAX_USAGE_AFFILIATE } from "../../utils/constants";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as UsersSvg } from "../../assets/icons/users.svg";
import { ReactComponent as GiftSvg } from "../../assets/icons/gift.svg";
import { ReactComponent as StarSvg } from "../../assets/icons/gradientStar.svg";
import { ReactComponent as CopySvg } from "../../assets/icons/copy.svg";
import { ReactComponent as SuccessSvg } from "../../assets/icons/success.svg";

const FreeCreditsModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [timeRemaining, setTimeRemaining] = useState({
    hours: 24,
    minutes: 0,
    seconds: 0,
  });
  const [copySuccess, setCopySuccess] = useState(false);

  const user = useSelector(selectUser);
  const referralLink = user?.affiliateLink;
  const loading = useSelector(selectAddDailyCreditsLoading);

  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useScrollLock(shouldRender);

  // Kontrola renderovanja i animacije
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsAnimating(false); // Početno stanje bez animacije
      // Kratka pauza da se modal renderuje pre animacije
      const timer = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      return () => cancelAnimationFrame(timer);
    } else if (shouldRender) {
      setIsAnimating(false);
      // Čeka da se animacija završi pre uklanjanja iz DOM-a
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // 300ms koliko traje animacija

      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  // Calculate remaining time based on lastFreeCredits
  useEffect(() => {
    if (!user?.lastFreeCredits) {
      setTimeRemaining({ hours: 24, minutes: 0, seconds: 0 });
      return;
    }

    const calculateRemainingTime = () => {
      const now = new Date();
      const lastClaim = new Date(user?.lastFreeCredits);
      const nextClaim = new Date(lastClaim.getTime() + 24 * 60 * 60 * 1000); // 24 hours later

      const timeDiff = nextClaim.getTime() - now.getTime();

      if (timeDiff <= 0) {
        // 24 hours have passed, can claim again
        return { hours: 0, minutes: 0, seconds: 0 };
      }

      const hours = Math.floor(timeDiff / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

      return { hours, minutes, seconds };
    };

    // Set initial time
    setTimeRemaining(calculateRemainingTime());

    // Update every second
    const interval = setInterval(() => {
      const remaining = calculateRemainingTime();
      setTimeRemaining(remaining);

      // If time reached 0, user can claim again
      if (
        remaining.hours === 0 &&
        remaining.minutes === 0 &&
        remaining.seconds === 0
      ) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, user?.lastFreeCredits]);

  if (!shouldRender) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopySuccess(true);
      toast.success("Referral link copied to clipboard!");
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
      toast.error("Failed to copy link. Please try again.");
    }
  };

  const handleCollectCredits = () => {
    dispatch(addDailyCredits())
      .unwrap()
      .then(() => {
        toast.success("Daily credits collected successfully! +5 credits");
      })
      .catch((err) => {
        toast.error("Failed to collect credits. " + err.message);
      });
  };

  const handleShare = async () => {
    const shareData = {
      title: "Join Playbox - NSFW AI Video Generation",
      text: "Create amazing NSFW AI videos with Playbox! Use my referral link to get started.",
      url: referralLink,
    };

    try {
      // Check if Web Share API is supported (mainly on mobile)
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success("Invite shared successfully!");
      } else {
        // Fallback: Copy to clipboard if Web Share API is not supported
        await navigator.clipboard.writeText(
          `${shareData.text} ${shareData.url}`
        );
        toast.success("Invite message copied to clipboard!");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        // User didn't cancel the share
        console.error("Error sharing:", err);
        toast.error("Failed to share invite. Please try again.");
      }
    }
  };

  const formatTime = (time) => String(time).padStart(2, "0");

  if (!user) {
    return null;
  }

  return (
    <ModalOverlay onClick={handleOverlayClick} $isAnimating={isAnimating}>
      <ModalContainer $isAnimating={isAnimating}>
        <ModalContent>
          <Row>
            <ModalTitle>Get Credits for Free</ModalTitle>
            <CloseButton onClick={onClose}>
              <CloseSvg />
            </CloseButton>
          </Row>
          <ModalSubtitle>
            Earn more credits by inviting friends or daily check-in.
          </ModalSubtitle>

          <Section>
            <GiftSvg width={20} height={20} color="#fff" />

            <SectionContent>
              <SectionTitle>Free Credits Daily</SectionTitle>
              <SectionDescription>
                {user?.lastFreeCredits &&
                !(
                  timeRemaining.hours === 0 &&
                  timeRemaining.minutes === 0 &&
                  timeRemaining.seconds === 0
                )
                  ? "Come back when timer reaches zero to claim more free credits."
                  : "Claim your daily free credits now!"}
              </SectionDescription>

              {!user?.lastFreeCredits ||
              (timeRemaining.hours === 0 &&
                timeRemaining.minutes === 0 &&
                timeRemaining.seconds === 0) ? (
                <CollectButton
                  onClick={handleCollectCredits}
                  disabled={loading}
                >
                  <StarSvg />
                  Claim Free Credits!
                </CollectButton>
              ) : (
                <>
                  <CollectButton disabled>
                    <StarSvg />
                    Claim Credits
                  </CollectButton>
                  <TimerContainer>
                    <TimerLabel>
                      Come back in: {formatTime(timeRemaining.hours)}:
                      {formatTime(timeRemaining.minutes)}:
                      {formatTime(timeRemaining.seconds)}
                    </TimerLabel>
                  </TimerContainer>
                </>
              )}
            </SectionContent>
          </Section>

          <Divider />

          <Section>
            <UsersSvg width={20} height={20} />

            <SectionContent>
              <SectionTitle>Referral Reward</SectionTitle>
              <SectionDescriptionFirst>
                Earn 5 credits for each friend who signs up!
              </SectionDescriptionFirst>
              {user?.usageOfAffiliate >= MAX_USAGE_AFFILIATE && (
                <SectionDescriptionSecond>
                  (Only the first 400 referrals grant you free credits.
                  Affiliate earnings from referral purchases are uncapped.)
                </SectionDescriptionSecond>
              )}

              <ReferralLinkContainer>
                <ReferralLink>
                  <span>{referralLink.replace("https://www.", "")}</span>
                </ReferralLink>
                <CopyButton onClick={handleCopyLink} $success={copySuccess}>
                  {copySuccess ? <SuccessSvg /> : <CopySvg />}
                </CopyButton>
              </ReferralLinkContainer>
              <StyledRow>
                <CollectButton $invite onClick={handleShare}>
                  Invite Friends
                </CollectButton>

                <ProgressContainer>
                  <ProgressBar>
                    <ProgressFill
                      $progress={
                        (Math.min(user?.usageOfAffiliate, 400) /
                          MAX_USAGE_AFFILIATE) *
                        100
                      }
                    />
                  </ProgressBar>
                  <ProgressText>
                    {user?.usageOfAffiliate} friends invited
                  </ProgressText>
                </ProgressContainer>
              </StyledRow>
            </SectionContent>
          </Section>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default FreeCreditsModal;
